import { ISchedule, IUserFitSimple } from '@gettactic/api';
import { IconValue } from '../../../components';
import { Furniture } from '../furniture-list';

export type ShapeTextAlignment = 'left' | 'center' | 'right';

export type ShapeType =
  | 'rectangle'
  | 'circle'
  | 'text'
  | 'svg'
  | 'icon'
  | 'group'
  | 'furniture'
  | 'spot'
  | 'image';

export type SpotShapeShape = 'default';

export const SHAPE_TYPE: Record<Uppercase<ShapeType>, ShapeType> = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  TEXT: 'text',
  SVG: 'svg',
  ICON: 'icon',
  GROUP: 'group',
  FURNITURE: 'furniture',
  SPOT: 'spot',
  IMAGE: 'image'
} as const;

export type LayerType = 'background' | 'floor' | 'shapes' | 'spots' | 'labels';
export type DrawShape = 'rectangle' | 'circle' | 'star';
export type SpotType = 'workspace' | 'desk' | 'meeting_room' | 'parking_lot';

export enum SpotShapeType {
  Node = 'workspace',
  Desk = 'desk',
  Room = 'meeting_room',
  Parking = 'parking_lot'
}

export const SpotShapeTypeToLabel: Record<SpotShapeType, string> = {
  workspace: 'Workspace',
  desk: 'Desk',
  meeting_room: 'Meeting Room',
  parking_lot: 'Parking Lot'
};

export interface ShapeTextOptions {
  fontSize?: number;
  fontFamily?: string;
  isBold?: boolean;
  isUnderline?: boolean;
  isItalic?: boolean;
  textColor?: string;
  textAlign?: ShapeTextAlignment;
}

export interface BaseShape {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  cornerRadius?: number;
  color?: string;
  hidden?: boolean;
  locked?: boolean;
  onClick?: () => void;
  layerType: LayerType;
  rotation?: number;
  layerLabel?: string;
}

export interface RectangleShape extends BaseShape {
  type: 'rectangle';
}

export interface CircleShape extends BaseShape {
  type: 'circle';
  radius: number;
}

export interface TextShape extends BaseShape, ShapeTextOptions {
  type: 'text';
  text: string;
}

export interface SVGShape extends BaseShape {
  type: 'svg';
  path: string;
  svgContent?: string;
  stroke: string;
  strokeWidth: number;
  scaleX?: number;
  scaleY?: number;
}

export interface ImageShape extends BaseShape {
  type: 'image';
  path: string;
}

export interface IconShape extends BaseShape, ShapeTextOptions {
  type: 'icon';
  icon: IconValue;
  scaleX?: number;
  scaleY?: number;
  text?: string;
}

export interface FurnitureShape extends BaseShape, ShapeTextOptions {
  type: 'furniture';
  furniture: Furniture;
  scaleX?: number;
  scaleY?: number;
  text?: string;
}

export interface GroupShape extends BaseShape {
  type: 'group';
  children: Shape[];
}

export type SpotOrientation = 'top' | 'bottom' | 'left' | 'right';
export type SpotLabelPlacement =
  | 'on-rect'
  | 'on-node'
  | 'outside-node'
  | 'outside'
  | 'hidden'
  | 'custom';
export type SpotLabelOrientation = 'auto' | 'top' | 'bottom';

export interface SpotShapeItem {
  id: string;
  spotAppearance?: SpotShapeType;
  spotShape?: SpotShapeShape;
  isInactive?: boolean;
  spotType: SpotShapeType;
  orientation: SpotOrientation;
  labelPlacement: SpotLabelPlacement;
  labelOrientation?: SpotLabelOrientation;
  textAlign: ShapeTextAlignment;
  spacing?: number;
  rowSpacing?: number;
  name?: string;
  rectProps?: {
    cornerRadius?: number;
    opacity?: number;
    color?: string;
  };
  circleProps?: {
    radius?: number;
    color?: string;
    stroke?: string;
    strokeWidth?: number;
  };
  textProps?: {
    text?: string;
    color?: string;
    fontSize?: number;
    fontFamily?: string;
    fontStyle?: string;
    rotation?: number;
    marginTop?: number;
    marginLeft?: number;
    width?: number;
  };
  resource_id?: string;
}

export interface SpotRowProps {
  id: string;
  items: SpotShapeItem[];
}

export interface SpotShape
  extends BaseShape,
    SpotShapeItem,
    Omit<ShapeTextOptions, 'textAlign'> {
  type: 'spot';
  rowsExtraSpacing: number;
  label: string;
  name?: string;
}
export interface SpotShape001 extends BaseShape, ShapeTextOptions {
  type: 'spot';
  orientation: SpotOrientation;
  labelPlacement: SpotLabelPlacement;
  spotType: SpotType;
  rows: SpotRowProps[];
  spacing: number;
  rowSpacing: number;
  rowsExtraSpacing: number;
}

export type Shape =
  | RectangleShape
  | TextShape
  | GroupShape
  | SVGShape
  | CircleShape
  | SpotShape
  | IconShape
  | FurnitureShape
  | ImageShape;

export interface DrawToolState {
  active: boolean;
  selectedShape: DrawShape;
  startPoint: { x: number; y: number } | null;
}

export interface MapCurrentPosition {
  scaleX: number;
  scaleY: number;
  x: number;
  y: number;
}

export interface MapInteractiveZoneActionOptions {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface MapInteractiveZone {
  zoneId: string;
  notAssigned: boolean;
  requiresApproval: boolean;
  disabled: boolean;
  reservedSlots: number;
  hasSchedules: boolean;
  actualSchedule?: ISchedule | null;
  scheduledUserId?: string;
  showCircleProfilePhoto: boolean;
  assignedUser?: IUserFitSimple | null;
  showPulse: boolean;
  isLastUsed?: boolean;
  isSelected?: boolean;
  lastAccessedDeskResourceId?: string | null;
  lastAccessedRoomResourceId?: string | null;
  onAction?: (options: MapInteractiveZoneActionOptions) => void;
}

export type MapInteractiveZones = Record<string, MapInteractiveZone>;

export interface ResourceEditingOptions {
  isOpen: boolean;
  resourceIds: string[];
}
